import { useMemo, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { STEP_ACTION_ICONS } from '@/components/processes/step-action-icons';
import {
  readSurveyToggles,
  type ProcessStepRecordingDisplay,
  type CapacityBinding
} from '@bliks-io/contracts';
import {
  Badge,
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Dialog,
  DialogContent,
  DataTable,
  ProcessFlowStepDetailSidebar,
  StepDescriptionCell,
  createColumnHeader,
  formatDate,
  TruncatedText,
  type DataTableColumnDef
} from '@bliks-io/ui';
import { formatFrequency as formatFrequencyLabel } from '@/lib/formatFrequency';
import { ProcessStepMembersCell } from './ProcessStepMembersCell';
import {
  HelperListCell,
  buildHelperSystemEntries,
  buildHelperPersonEntries,
  buildHelperOrgFunctionEntries,
  buildHelperDocumentEntries,
  type HelperEntry
} from './ProcessStepHelperColumns';
import { buildAnalyticsColumns } from './ProcessStepManagerColumns';
import { useAssigneeLookup } from '@/lib/useAssigneeLookup';
import { useOrgSettings } from '@/api/queries/useOrgSettings';

interface ProcessStepsTableProps {
  steps: ProcessStepRecordingDisplay[];
  stepCapacities?: Record<string, CapacityBinding>;
  /** Per-step response coverage (ADR 0023 decision 4), 0..1 by step id. */
  stepCoverage?: Record<string, number> | null;
  isLoading?: boolean;
  showManagerColumns?: boolean;
  showVariantCheck?: boolean;
}

export function ProcessStepsTable({
  steps,
  stepCapacities,
  stepCoverage,
  isLoading,
  showManagerColumns = false,
  showVariantCheck = false
}: ProcessStepsTableProps) {
  const { t } = useTranslation();
  const assigneeLookup = useAssigneeLookup();
  const { data: orgSettings } = useOrgSettings();
  const variantCheckEnabled = readSurveyToggles(orgSettings).variantCheck;
  const [previewStep, setPreviewStep] = useState<ProcessStepRecordingDisplay | null>(null);

  // Sort steps by step number for chronological display
  const sortedSteps = useMemo(() => {
    if (!steps || steps.length === 0) return [];
    return [...steps].sort((a, b) => {
      const aNum = a.stepNumber ?? Number.MAX_SAFE_INTEGER;
      const bNum = b.stepNumber ?? Number.MAX_SAFE_INTEGER;
      return aNum - bNum;
    });
  }, [steps]);

  // Helper-entry builders bound to the current lookup, keyed by column. Shared
  // by the filter-option memo and the column definitions so a step's helpers
  // resolve identically in the cell, the accessor and the filter.
  const helperBuilders = useMemo(
    () => ({
      helperSystems: (step: ProcessStepRecordingDisplay) =>
        buildHelperSystemEntries(step, assigneeLookup),
      helperPersons: (step: ProcessStepRecordingDisplay) =>
        buildHelperPersonEntries(step, assigneeLookup),
      helperOrgFunctions: (step: ProcessStepRecordingDisplay) =>
        buildHelperOrgFunctionEntries(step, assigneeLookup),
      helperDocuments: (step: ProcessStepRecordingDisplay) => buildHelperDocumentEntries(step)
    }),
    [assigneeLookup]
  );

  // The distinct helpers used anywhere in this process, per kind — the option
  // lists for the columns' select filters. Deduped by id (name kept for the
  // label), sorted by name so the dropdowns read alphabetically.
  const helperFilterOptions = useMemo(() => {
    const collect = (build: (step: ProcessStepRecordingDisplay) => HelperEntry[]) => {
      const byId = new Map<string, string>();
      for (const step of sortedSteps) {
        for (const entry of build(step)) {
          if (!byId.has(entry.key)) byId.set(entry.key, entry.name);
        }
      }
      return Array.from(byId, ([value, label]) => ({ value, label })).sort((a, b) =>
        a.label.localeCompare(b.label)
      );
    };
    return {
      helperSystems: collect(helperBuilders.helperSystems),
      helperPersons: collect(helperBuilders.helperPersons),
      helperOrgFunctions: collect(helperBuilders.helperOrgFunctions),
      helperDocuments: collect(helperBuilders.helperDocuments)
    };
  }, [sortedSteps, helperBuilders]);

  const formatFrequency = useCallback(
    (frequency?: string, timesPerPeriod?: number | null) => {
      if (!frequency) return 'N/A';
      return formatFrequencyLabel(frequency, timesPerPeriod, t);
    },
    [t]
  );

  const formatDuration = useCallback(
    (durationInMinutes?: number) => {
      if (!durationInMinutes) return 'N/A';
      return t('processes.minutesValue', { value: Math.round(durationInMinutes * 100) / 100 });
    },
    [t]
  );

  const formatInputs = (inputs: ProcessStepRecordingDisplay['inputs']) => {
    if (!inputs || inputs.length === 0) return [];

    return inputs.map((input) => {
      if (typeof input === 'string') {
        return input;
      }
      return input.name || input.description;
    });
  };

  const columns = useMemo<DataTableColumnDef<ProcessStepRecordingDisplay>[]>(() => {
    // The Helfer columns share one shape, differing only by which builder
    // resolves the step's helpers and which filter options they offer.
    const makeHelperColumn = (
      id: keyof typeof helperBuilders,
      labelKey: string
    ): DataTableColumnDef<ProcessStepRecordingDisplay> => {
      const build = helperBuilders[id];
      return {
        id,
        accessorFn: (row) =>
          build(row)
            .map((e) => e.name)
            .join(', '),
        meta: {
          label: t(labelKey),
          filterType: 'select',
          filterOptions: helperFilterOptions[id]
        },
        header: createColumnHeader(t(labelKey)),
        cell: ({ row }) => <HelperListCell entries={build(row.original)} />,
        enableColumnFilter: true,
        filterFn: (row, _columnId, value: string) =>
          build(row.original).some((e) => e.key === value)
      };
    };

    const baseColumns: DataTableColumnDef<ProcessStepRecordingDisplay>[] = [
      {
        id: 'stepNumber',
        accessorFn: (row) => row.stepNumber,
        meta: { label: t('processes.stepNumber') },
        header: createColumnHeader(t('processes.stepNumber')),
        cell: ({ row }) => {
          const stepNum = row.original.stepNumber;
          return (
            <div className="flex items-center justify-center">
              <span className="text-sm font-semibold text-black">#{stepNum ?? '?'}</span>
            </div>
          );
        }
      },
      {
        id: 'actionType',
        accessorFn: (row) => row.actionType,
        meta: { label: t('processes.type') },
        header: createColumnHeader(t('processes.type')),
        cell: ({ row }) => {
          const actionType = row.original.actionType;
          const isDecision = actionType === 'decision';
          const Icon = STEP_ACTION_ICONS[actionType];

          return (
            <Badge
              variant={isDecision ? 'default' : 'outline'}
              className={`flex items-center gap-1 w-fit ${
                isDecision
                  ? 'bg-highlight-surface-strong text-highlight border-highlight-line'
                  : 'bg-info-surface text-info border-info-line'
              }`}
            >
              <Icon className="h-3 w-3" />
              {isDecision ? t('processes.decision') : t('processes.task')}
            </Badge>
          );
        }
      },
      {
        accessorKey: 'taskDescription',
        meta: { label: t('processes.processStep') },
        header: createColumnHeader(t('processes.processStep')),
        cell: ({ row }) => {
          const isIncomplete = row.original.status !== 'finalized';
          return (
            <StepDescriptionCell
              actionType={row.original.actionType}
              generatedName={row.original.generatedName}
              taskDescription={row.original.task?.taskDescription}
              ruleCount={row.original.rules?.length}
              isIncomplete={isIncomplete}
              onPreviewClick={() => setPreviewStep(row.original)}
            />
          );
        }
      }
    ];

    // Analytics columns after taskDescription; their order is the builder's.
    // A tenant that never asks the Variant Check reads 0/N.
    baseColumns.push(
      ...buildAnalyticsColumns({
        t,
        showManagerColumns,
        showVariantCheck: showVariantCheck && variantCheckEnabled,
        stepCapacities,
        stepCoverage,
        formatFrequency,
        formatDuration
      })
    );

    // Add remaining base columns
    baseColumns.push(
      {
        accessorKey: 'inputs',
        meta: { label: t('processes.inputs') },
        header: createColumnHeader(t('processes.inputs')),
        cell: ({ row }) => {
          const inputs = formatInputs(row.original.inputs);
          return (
            <div className="space-y-1 max-w-xs">
              {inputs.length > 0 ? (
                inputs.map((input, idx) => (
                  <div key={idx} className="flex text-sm font-medium">
                    <span className="text-gray-8 mr-2">{idx + 1}.</span>
                    <TruncatedText text={input} className="min-w-0 flex-1" />
                  </div>
                ))
              ) : (
                <span className="text-sm text-gray-8">{t('common.notAvailable')}</span>
              )}
            </div>
          );
        }
      },
      {
        id: 'output',
        accessorFn: (row) => row.task?.output,
        meta: { label: t('processes.output') },
        header: createColumnHeader(t('processes.output')),
        cell: ({ row }) => {
          const isDecision = row.original.actionType === 'decision';

          if (isDecision) {
            // For decisions, collect all unique outputs from rules
            const rules = row.original.rules || [];
            const outputs = rules.map((rule) => rule.output).filter(Boolean);
            const uniqueOutputs = [...new Set(outputs)];

            if (uniqueOutputs.length === 0) {
              return <span className="text-sm text-gray-8">{t('common.notAvailable')}</span>;
            }

            return (
              <div className="space-y-1 max-w-xs">
                {uniqueOutputs.map((output, idx) => (
                  <div key={idx} className="flex text-sm font-medium">
                    <span className="text-gray-8 mr-2">{idx + 1}.</span>
                    <TruncatedText text={output} className="min-w-0 flex-1" />
                  </div>
                ))}
              </div>
            );
          }

          // For tasks, use task output
          const output = row.original.task?.output;
          return <span className="text-sm font-medium">{output || 'N/A'}</span>;
        }
      },
      {
        id: 'members',
        accessorFn: (row) => row.recordedBy,
        meta: { label: t('processes.processStepMembers') },
        header: createColumnHeader(t('processes.processStepMembers')),
        cell: ({ row }) => <ProcessStepMembersCell step={row.original} />
      },
      makeHelperColumn('helperSystems', 'processes.helperSystems'),
      makeHelperColumn('helperPersons', 'processes.helperPersons'),
      makeHelperColumn('helperOrgFunctions', 'processes.helperOrgFunctions'),
      makeHelperColumn('helperDocuments', 'processes.helperDocuments'),
      {
        id: 'lastChange',
        accessorFn: (row) => row.updatedAt,
        meta: { label: t('processes.lastChange') },
        header: createColumnHeader(t('processes.lastChange')),
        cell: ({ row }) => {
          const recordedBy = row.original.recordedBy;
          // recordedBy is null when its user was hard-deleted (dangling ref).
          const recordedByName = recordedBy
            ? `${recordedBy.firstName} ${recordedBy.lastName}`
            : t('viralRecording.labels.unknownUser');
          return (
            <div className="flex flex-col items-start">
              <div className="text-xs">
                <span className="font-bold">{formatDate(row.original.updatedAt)}</span>
              </div>
              <div className="text-xs font-medium text-gray-8">
                {t('processes.stepRecordedBy', { name: recordedByName })}
              </div>
            </div>
          );
        }
      }
    );

    return baseColumns;
  }, [
    t,
    showManagerColumns,
    showVariantCheck,
    variantCheckEnabled,
    stepCapacities,
    stepCoverage,
    formatFrequency,
    formatDuration,
    helperBuilders,
    helperFilterOptions
  ]);

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('processes.processSteps')}</CardTitle>
      </CardHeader>
      <CardContent>
        <DataTable
          columns={columns}
          data={sortedSteps}
          isLoading={isLoading}
          emptyMessage={t('processes.noSteps')}
          enableColumnVisibility
          enableColumnFilters
          enableUrlState
          tableId="process-steps"
          initialColumnVisibility={{
            actionType: false,
            variants: false,
            helperSystems: false,
            helperPersons: false,
            helperOrgFunctions: false,
            helperDocuments: false
          }}
        />
      </CardContent>

      <Dialog open={!!previewStep} onOpenChange={(open) => !open && setPreviewStep(null)}>
        <DialogContent className="max-w-lg max-h-[85vh] p-0 overflow-hidden [&>button]:hidden flex flex-col">
          {previewStep && (
            <ProcessFlowStepDetailSidebar
              step={previewStep}
              onClose={() => setPreviewStep(null)}
              showSensitiveFields={showManagerColumns}
              showVariantCheck={showVariantCheck}
              assigneeLookup={assigneeLookup}
            />
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
}
